import React, { FC, useCallback, useMemo } from 'react';
import { GestureResponderEvent, TouchableOpacityProps } from 'react-native';
import styled from 'styled-components/native';

import { EOrderType } from '@webtrader/core/api/order';
import { BUY_ORDER_TYPES, SELL_ORDER_TYPES } from '@webtrader/core/constants/order';
import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { useTheme } from '@webtrader/core/services/theme/v2';
import { rgba } from '@webtrader/core/themes/utils';

import Spinner from '@/components/Loaders/Spin';
import Text from '@/components/Text';
import TouchableOpacity from '@/components/Touch/Opacity';

type ButtonVariant = 'default' | 'subtle' | 'light' | 'border';
type ButtonTextColor = 'default' | 'light';

interface IProps extends TouchableOpacityProps {
  height?: number;
  loading?: boolean;
  variant?: ButtonVariant;
  stretch?: boolean;
  orderType?: EOrderType;
  textColor?: ButtonTextColor;
  showOrderTypeColors?: boolean;
  testID?: EDataTestId;
}

interface IBaseTextProps extends IProps {
  color: string;
}

interface IBaseButtonProps extends IProps {
  backgroundColor: string;
  border: string;
}

const Button: FC<IProps> = (props) => {
  const {
    height = 46,
    loading = false,
    variant = 'default',
    disabled = false,
    orderType,
    textColor,
    showOrderTypeColors = true,
    children,
    testID,
    onPress,
    ...restProps
  } = props;

  const { theme } = useTheme();

  const loaderColor = useMemo(() => {
    switch (variant) {
      case 'subtle':
      case 'light':
        return theme.colors.primary;
      default:
        return 'white';
    }
  }, [theme, variant]);

  const orderTypeColor = useMemo(() => {
    if (orderType !== undefined) {
      if (!showOrderTypeColors) {
        return theme.colors.background[2];
      }
      if (BUY_ORDER_TYPES.includes(orderType)) {
        return rgba(theme.colors.pnl.green, disabled ? 0.4 : 1);
      }
      if (SELL_ORDER_TYPES.includes(orderType)) {
        return rgba(theme.colors.pnl.red, disabled ? 0.4 : 1);
      }
    }
    return theme.colors.background[2];
  }, [disabled, orderType, showOrderTypeColors, theme.colors.background, theme.colors.pnl.green, theme.colors.pnl.red]);

  const customBackgroundColor = useMemo(() => {
    if (variant === 'border') {
      return theme.colors.background[0];
    }

    if (orderType !== undefined) {
      return orderTypeColor;
    }

    switch (variant) {
      case 'light':
        return rgba(theme.colors.background[2], disabled ? 0.3 : 1);
      case 'subtle':
        return 'transparent';
      default:
        return rgba(theme.colors.primary, disabled ? 0.3 : 1);
    }
  }, [variant, orderType, orderTypeColor, theme.colors.background, theme.colors.primary, disabled]);

  const customTextColor = useMemo(() => {
    if (orderType !== undefined) {
      if (showOrderTypeColors) {
        switch (variant) {
          case 'border':
            return orderTypeColor;
          default:
            return 'white';
        }
      }
    }

    switch (textColor) {
      case 'light':
        return rgba(theme.colors.text[2], disabled ? 0.4 : 1);
      default:
        break;
    }

    switch (variant) {
      case 'light':
        return rgba(theme.colors.text[0], disabled ? 0.4 : 1);
      case 'border':
      case 'subtle':
        return rgba(theme.colors.primary, disabled ? 0.4 : 1);
      default:
        return rgba('rgb(255, 255, 255)', disabled ? 0.6 : 1);
    }
  }, [
    orderType,
    textColor,
    variant,
    showOrderTypeColors,
    orderTypeColor,
    theme.colors.text,
    theme.colors.primary,
    disabled
  ]);

  const customBorder = useMemo(() => {
    if (variant === 'border') {
      let borderColor = theme.colors.primary;
      if (orderType !== undefined) {
        borderColor = orderTypeColor;
      }
      return `1px solid ${borderColor}`;
    }
    return 'none';
  }, [orderType, orderTypeColor, theme.colors.primary, variant]);

  const onPressButton = useCallback(
    (event: GestureResponderEvent) => {
      if (loading) {
        return;
      }

      onPress?.(event);
    },
    [loading, onPress]
  );

  const buttonChildren = useMemo(() => {
    if (typeof children === 'string') {
      return (
        <BaseButtonText disabled={disabled} color={customTextColor}>
          {children}
        </BaseButtonText>
      );
    }

    return children;
  }, [disabled, children, customTextColor]);

  return (
    <BaseButton
      {...restProps}
      height={height}
      disabled={disabled}
      backgroundColor={customBackgroundColor}
      border={customBorder}
      testID={testID}
      onPress={onPressButton}
    >
      {loading ? <Spinner color={loaderColor} size={dynamicSize.convertSize(28)} /> : buttonChildren}
    </BaseButton>
  );
};

export default Button;

const BaseButton = styled(TouchableOpacity)<IBaseButtonProps>`
  height: ${({ height }) => dynamicSize.convertSize(height)}px;

  ${({ stretch }) => stretch && 'flex: 1; width: 100%;'}

  align-items: center;
  justify-content: center;

  border: ${({ border }) => border};
  border-radius: ${dynamicSize.convertSize(4)}px;
  background-color: ${({ backgroundColor }) => backgroundColor};
`;

const BaseButtonText = styled(Text)<IBaseTextProps>`
  color: ${({ color }) => color};
  font-size: ${dynamicSize.convertSize(14)}px;
  font-weight: 500;
  text-align: center;

  flex-direction: column;
`;
